import { useMutation } from '@apollo/client';
import * as React from 'react';

import { GENERAL_VALUATION_MUTATION } from '../../../../client/__graphql__/mutations/valuation';
import { useForm } from '../../../utils/hooks/useForm';
import { useFormTransition } from '../../../utils/hooks/useFormTransition';
import { createGTMEvent } from '../../../utils/tracking/gtm';
import { LeadType, PageType } from '../../../__types__/generated/globalTypes';
import { Section } from '../../layout/Section';
import { Thanks } from '../../layout/Thanks';
import { Button } from '../../ui/button/Button';
import { ButtonGroup } from '../../ui/button/ButtonGroup';
import { Form, FormWrapper, FormRow } from '../../ui/form/Form';
import { FormError } from '../../ui/form/FormError';
import { Input } from '../../ui/form/Input';
import { ButtonLink } from '../../ui/link/ButtonLink';
import { Heading } from '../../ui/text/Heading';
import { Paragraph } from '../../ui/text/Paragraph';
import { useDigtectiveContext } from '../../../__lib__/digtective/context/hooks/useDigtectiveContext';

export const ArticleForm: React.FC = () => {
  const [posted, setPosted] = React.useState(false);
  const { submitWithDigger } = useDigtectiveContext();
  const { fadeIn, fadeOut } = useFormTransition({
    posted
  });

  const [lead, { error, loading }] = useMutation(GENERAL_VALUATION_MUTATION, {
    onCompleted: ({ lead }) => {
      if (lead.valuation?.success) {
        setPosted(true);
        createGTMEvent({
          event: 'pmFormSubmission',
          gaEvent: 'RT_VALUATION',
          gaCategory: 'RT_VALUATION',
          gaAction: 'RT_SEND_VALUATION'
        });
      }
    }
  });

  const { fields, handleSubmit, handleChange } = useForm({
    fields: [
      {
        type: 'text',
        name: 'name',
        placeholder: 'Eks. Ola Nordmann',
        value: '',
        label: 'Navn *',
        required: true
      },
      {
        type: 'tel',
        name: 'phone',
        placeholder: 'Eks. 00000000',
        value: '',
        label: 'Telefon *',
        labelAfter: '(Brukes til å kontakte deg)',
        required: true
      },
      {
        type: 'text',
        name: 'zip',
        placeholder: 'Eks. 00000000',
        value: '',
        label: 'Postnummer *',
        labelAfter: '(Brukes til å kontakte deg)',
        required: true
      },
      {
        type: 'email',
        name: 'email',
        placeholder: 'Eks. ole@mail.no',
        value: '',
        label: 'E-post *',
        labelAfter: '(Brukes til å kontakte deg)',
        required: true
      }
    ],
    submit: (fields) => {
      submitWithDigger({ zip: fields.zip }, ({ diggerId }) => {
        lead({
          variables: {
            input: {
              ...fields,
              diggerId,
              referer: `${location.pathname}${location.search}`,
              leadType: LeadType.VERDIVURDERING,
              pageType: PageType.GENERELL
            }
          }
        });
      });
    }
  });

  return (
    <>
      <FormWrapper style={fadeOut}>
        <Section className="form-section" style={{ paddingBottom: '10px' }}>
          <Heading tag="h2" center={true}>
            Bli kontaktet
          </Heading>
          <Paragraph center={true} className="form-text">
            Ønsker du å vite mer om hvordan vi kan hjelpe deg?
          </Paragraph>
          <Paragraph center={true} className="form-text">
            Fyll ut skjemaet og en av våre meglere i ditt nærområde kontakt.
          </Paragraph>
        </Section>
        <Form onSubmit={handleSubmit} noValidate>
          {error &&
            error.graphQLErrors.map((error, index) => (
              <FormError key={`error_${index}`} message={error.message} />
            ))}
          {fields.map((item) => {
            if (item) {
              return (
                <FormRow key={`field_${item.name}`}>
                  <Input
                    type={item.type}
                    name={item.name}
                    label={item.label}
                    labelAfter={item.labelAfter}
                    placeholder={item.placeholder}
                    value={item.value}
                    onChange={handleChange}
                    error={item.error}
                  />
                </FormRow>
              );
            }
          })}
          <ButtonGroup>
            <Button
              type="primary"
              colorTheme="gold"
              disabled={loading}
              loading={loading}
            >
              Kontakt meg
            </Button>
            <ButtonLink
              as="a"
              href="/personvern"
              className="privacy-link-mobile"
            >
              Personvernpolicy
            </ButtonLink>
          </ButtonGroup>
        </Form>
      </FormWrapper>
      <Thanks style={fadeIn}>
        <Section className="form-section">
          <Heading tag="h1" center={true}>
            Tusen takk
          </Heading>
          <Paragraph center={true}>
            En eiendomsmegler vil ta kontakt med deg så snart som mulig.
          </Paragraph>
        </Section>
      </Thanks>
    </>
  );
};
